import Dropdown from "../../components/Dropdown"
import DatePicker from "../../components/DatePicker"

export const dateRangeOptions = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "All",
  "Custom",
]

export const statusOptions = ["All", "Success", "Errored"]

// Built from local date parts, not toISOString() — that converts to UTC first,
// which rolls the date back a day in timezones ahead of UTC.
export const toISODateString = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

export const parseISODate = (isoDate) =>
  isoDate ? new Date(`${isoDate}T00:00:00`) : null

const datePickerClass =
  "outline-none border-0 bg-transparent text-sm leading-5 text-gray-900 w-full"
const datePickerWrapperClass =
  "rounded-md border border-gray-300 bg-white py-2 pl-3 pr-2 flex items-center gap-2 hover:cursor-pointer focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
const clearButtonClass =
  "px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"

/*
  The filter card.

  An active accession search overrides the filters rather than combining with
  them, so while one is set every filter is disabled and blanked, with a title
  saying how to get them back.

  Do not wrap this in an `overflow-hidden` container: the Custom range's
  calendars render in a popper that would be clipped by it.
*/
const VideoLogsFilterBar = ({
  dateRange,
  dateFrom,
  dateTo,
  status,
  patientIssuer,
  issuerOptions,
  searchTerm,
  isSearchActive,
  showCustomDateRange,
  onDateRange,
  onDateFrom,
  onDateTo,
  onStatus,
  onPatientIssuer,
  onSearchTerm,
  onSearch,
  onClearSearch,
  onClearFilters,
}) => {
  const disabledTitle = isSearchActive
    ? "Clear the search field to enable filters"
    : null

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <div className="flex flex-wrap gap-4">
        <div>
          <Dropdown
            disabled={isSearchActive}
            label="DATE RANGE"
            options={isSearchActive ? [""] : dateRangeOptions}
            setValue={onDateRange}
            title={disabledTitle || "Filter by date range"}
            value={isSearchActive ? "" : dateRange}
          />
        </div>

        {showCustomDateRange && (
          <>
            <div>
              <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
                FROM
              </label>
              <DatePicker
                className={datePickerClass}
                maxDate={dateTo ? parseISODate(dateTo) : new Date()}
                placeholder="From date"
                value={dateFrom ? parseISODate(dateFrom) : null}
                wrapperClassName={datePickerWrapperClass}
                onChange={(date) =>
                  onDateFrom(date ? toISODateString(date) : "")
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
                TO
              </label>
              <DatePicker
                className={datePickerClass}
                maxDate={new Date()}
                minDate={dateFrom ? parseISODate(dateFrom) : null}
                placeholder="To date"
                value={dateTo ? parseISODate(dateTo) : null}
                wrapperClassName={datePickerWrapperClass}
                onChange={(date) => onDateTo(date ? toISODateString(date) : "")}
              />
            </div>
          </>
        )}

        <div>
          <Dropdown
            disabled={isSearchActive}
            label="STATUS"
            options={isSearchActive ? [""] : statusOptions}
            setValue={onStatus}
            title={disabledTitle || "Filter by success/error status"}
            value={isSearchActive ? "" : status}
          />
        </div>

        <div>
          <Dropdown
            disabled={isSearchActive}
            label="PATIENT ISSUER"
            options={isSearchActive ? [""] : issuerOptions}
            setValue={onPatientIssuer}
            title={disabledTitle || "Filter by patient issuer"}
            value={isSearchActive ? "" : patientIssuer}
          />
        </div>

        <div className="flex items-end gap-2">
          <button
            className={clearButtonClass}
            disabled={isSearchActive}
            title={disabledTitle || "Clear all filters"}
            onClick={onClearFilters}
          >
            Clear
          </button>
        </div>

        <div className="flex-1" />

        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
              ACCESSION NUMBER
            </label>
            <input
              className="w-full min-w-[300px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. AN-2026-00142"
              type="text"
              value={searchTerm}
              onChange={(event) => onSearchTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  onSearch()
                }
              }}
            />
          </div>
          <button
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-blue-400"
            onClick={onSearch}
          >
            Search
          </button>
          <button className={clearButtonClass} onClick={onClearSearch}>
            Clear
          </button>
        </div>
      </div>
    </div>
  )
}

export default VideoLogsFilterBar
